import type { ElementType } from 'react';
import { isNonProd } from '@/lib/appStage';
import { PERMISSION, hasAdminAccess } from '@/lib/permissions';
import { ACCESS_LABS_MODULE_KEY } from '@/lib/accessLabs';
import { COMM_CENTER_NAV_HREF, MENTIONS_NAV_HREF } from '@/components/layout/navBadges';
import { adminPageLevelOf, type AdminPageLevelMap } from '@/hooks/useAdminPageLevels';
import {
  AtSign,
  Contact,
  ListChecks,
  ScrollText,
  Link2,
  Palette,
  Tags,
  Truck,
  XCircle,
  ListVideo,
  MapPin,
  Tv,
  BarChart2,
  Bell,
  BellRing,
  Building2,
  BookMarked,
  BookOpen,
  FileSpreadsheet,
  Calendar,
  CalendarDays,
  CheckSquare,
  Clipboard,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Crown,
  DatabaseZap,
  FileText,
  FlaskConical,
  FolderArchive,
  FormInput,
  Clapperboard,
  Download,
  Gift,
  Syringe,
  HelpCircle,
  Inbox,
  Kanban,
  Languages,
  LayoutDashboard,
  Mail,
  MailOpen,
  Megaphone,
  MessageCircle,
  MessageSquare,
  Package,
  Percent,
  Pill,
  Plug,
  Printer,
  Receipt,
  Search,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Stethoscope,
  Store,
  Ticket,
  Timer,
  UserCheck,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  Share2,
  Video,
  Warehouse,
  Boxes,
  Layers,
  Banknote,
  ShieldAlert,
  Landmark,
  Zap,
  FileSignature,
  TerminalSquare,
  Wallet,
  FileWarning,
  MonitorSmartphone,
  Phone,
  Images,
} from 'lucide-react';
import { MARKETPLACE_MODULE_KEYS } from '@/pages/marketplace/marketplaceCatalog';

export interface NavItemDef {
  label: string;
  icon: ElementType;
  href?: string;
  external?: boolean;
  minUserLevel?: number;
  /**
   * When set, the user must have this permission ID in their permissions array.
   * Maps to legacy `users_permissions.permissionID`.
   */
  requiredPermission?: number;
  /**
   * When true, only super admins (`useAuth().isSuperAdmin`) see this entry —
   * regardless of `minUserLevel`.  Use for items that target the corporate
   * support team, not customer admins.
   */
  requiresSuperAdmin?: boolean;
  /**
   * When true, only staff whose corp SSO-bridged session resolved
   * `master_users.permissions.is_admin = true` (`useAuth().isMasterAdmin`)
   * see this entry — regardless of `minUserLevel`. Direct practice logins
   * never satisfy this, since they have no master_users identity at all.
   */
  requiresMasterAdmin?: boolean;
  /**
   * When true, only users with Admin Access (`hasAdminAccess`: level 5, or
   * permission 36) see this entry. Replaces `minUserLevel` on the Admin area —
   * a level-4 user without the grant does not get in.
   */
  requiresAdminAccess?: boolean;
  /**
   * When set, this entry is hidden if the named Corp-App module (Module
   * Settings) is disabled for the account. Use for admin integration pages
   * whose href does not share the module's base path — e.g.
   * `/admin/marketplace/axiom` is gated by the "axiom" module, which the
   * base-path matcher can't infer from the URL.
   */
  requiredModuleKey?: string;
  /**
   * Fail-CLOSED module gate: this entry renders only when the named module has
   * an explicitly ACTIVE aa_accounts_modules row (`explicit` in the account
   * modules payload). Use for opt-in surfaces (e.g. Facilities) that must stay
   * invisible to every account that hasn't deliberately enabled them —
   * `requiredModuleKey` alone fails open when no rows exist.
   */
  requiresModuleActive?: string;
  /**
   * The Admin page this menu entry is: its key in the Admin page catalog
   * (api/resources/admin-pages.json, checked by adminPages.drift.test.ts). The
   * entry shows only when the account's level for the key is Editable or
   * Locked, from a fresh `/account/modules` fetch (useAdminPageLevels); the
   * route carries the same key on RequireAdminPage.
   *
   * Named explicitly rather than derived from the href, because the key is the
   * page's canonical route and an href can be an alias (Lead Sources links to
   * /admin/referrers). Super-admin-only entries (Lab Uploads, Zenoti) have no
   * key and no level.
   */
  adminPage?: string;
  /**
   * For a GROUP whose own page gathers several opt-in apps (the Marketplace
   * dashboard): the group's href is kept only when at least one of these
   * modules has an explicitly active row. With none active the group falls back
   * to a plain expander over its surviving children, and disappears only if
   * none survive — so the page never opens empty and existing links stay.
   */
  requiresAnyModuleActive?: string[];
  children?: NavItemDef[];
}

export const NAV_ITEMS: NavItemDef[] = [
  {
    label: 'Dashboard',
    icon: LayoutDashboard,
    href: '/',
    requiredModuleKey: 'dashboard',
    children: [
      { label: 'Inbox', icon: Inbox, href: '/inbox' },
    ],
  },
  // Directly under Dashboard so it is visible without scrolling, and a top-level
  // leaf (not an Inbox sibling) so it keeps its own icon and count in the
  // collapsed rail.
  { label: 'Alerts', icon: Bell, href: '/alerts', requiredModuleKey: 'alerts' },
  // Staging-only (and local dev), hidden in production until ready, AND gated
  // on the "AI Assistant" permission (id 26) via the requiredPermission filter.
  ...(isNonProd()
    ? [{ label: 'AI Assistant', icon: Sparkles, href: '/ai-assistant', requiredPermission: 26, requiredModuleKey: 'ai assistant' } satisfies NavItemDef]
    : []),
  { label: 'Patients', icon: Users, href: '/patients' },
  {
    label: 'CRM',
    icon: Kanban,
    href: '/crm',
    requiredModuleKey: 'crm',
    children: [
      { label: 'Pipeline', icon: Kanban, href: '/crm/pipeline' },
      { label: 'Leads', icon: UserPlus, href: '/leads' },
      { label: 'Inquiries', icon: Search, href: '/plans?view=inquiries' },
      // Consults: TWO independent gates, both required and both mirrored
      // server-side in ConsultsController, exactly like Surgeries and Post-ops.
      // `requiresModuleActive` is fail-CLOSED (the `consults` module is opt-in
      // per practice), and permission 5 "Consult Portal" is the per-user grant.
      { label: 'Consults', icon: ClipboardList, href: '/consults', requiresModuleActive: 'consults', requiredPermission: PERMISSION.CONSULTS },
      // Surgeries: the pre-op worklist. TWO independent gates, both required and
      // both mirrored server-side in SurgeryController. `requiresModuleActive` is
      // fail-CLOSED because the module is opt-in -- in legacy this section had no
      // account switch at all, just a hardcoded accountID allowlist in PHP -- and
      // permission 6 is the per-user grant legacy called `$consults_access`.
      // Both nav filters recurse into children, so the gates hold here as they
      // did at the top level.
      { label: 'Surgeries', icon: Stethoscope, href: '/surgery', requiresModuleActive: 'surgery', requiredPermission: PERMISSION.SURGERY },
      // Fail-CLOSED: `requiresModuleActive` needs an explicitly ACTIVE
      // aa_accounts_modules row, unlike `requiredModuleKey` which fails open.
      {
        label: 'Post-ops',
        icon: Stethoscope,
        href: '/post-ops',
        requiresModuleActive: 'post ops',
        requiredPermission: PERMISSION.POST_OPS,
      },
      { label: 'Plans', icon: Clipboard, href: '/plans' },
    ],
  },
  {
    label: 'Marketing',
    icon: Megaphone,
    href: '/marketing',
    requiredPermission: PERMISSION.MARKETING,
    requiredModuleKey: 'marketing',
    children: [
      {
        // Ads/attribution side of marketing (fka "Command Center") — clicking
        // the group itself opens the ads dashboard; Integrations moved to
        // Admin → Integrations → Marketing.
        label: 'Ads Center',
        icon: LayoutDashboard,
        href: '/marketing/ads',
        children: [
          { label: 'AI Explorer', icon: Sparkles, href: '/marketing/ai-explorer' },
        ],
      },
      {
        // Outbound campaign tooling (fka "Tools") — clicking the group itself
        // opens the campaign Reports page (its de-facto dashboard).
        label: 'Campaigns',
        icon: Package,
        href: '/marketing/reports',
        children: [
          { label: 'Email Campaigns', icon: Mail, href: '/marketing/email-campaigns', requiredPermission: PERMISSION.MARKETING_CAMPAIGNS },
          { label: 'SMS Campaigns', icon: MessageCircle, href: '/marketing/sms-campaigns', requiredPermission: PERMISSION.MARKETING_CAMPAIGNS },
          { label: 'Push Campaigns', icon: BellRing, href: '/marketing/push-campaigns', requiredPermission: PERMISSION.MARKETING_PUSH },
          { label: 'Automations', icon: Zap, href: '/marketing/automations', requiredPermission: PERMISSION.MARKETING_AUTOMATIONS },
          { label: 'Templates', icon: FileText, href: '/marketing/templates' },
          { label: 'Contact Lists', icon: UsersRound, href: '/marketing/contact-lists' },
          { label: 'App Posts', icon: FileText, href: '/marketing/posts', requiredPermission: PERMISSION.MARKETING_APP_POSTS },
          { label: 'App Activity', icon: Smartphone, href: '/marketing/app-activity', requiredPermission: PERMISSION.MARKETING_APP_POSTS },
          { label: 'Settings', icon: Settings, href: '/marketing/settings', requiredPermission: PERMISSION.MARKETING_SETTINGS },
        ],
      },
      {
        // "Apple TV", not "FranchiseSign": staff know the box on the wall, not
        // the vendor. Fail-CLOSED on an explicitly active module row, so only
        // practices actually running signage ever see it.
        label: 'Apple TV',
        icon: Tv,
        href: '/marketing/apple-tv',
        requiresModuleActive: 'franchisesign',
        requiredPermission: PERMISSION.APPLE_TV,
        children: [
          { label: 'Get Started', icon: Download, href: '/marketing/apple-tv/get-started' },
          { label: 'Locations', icon: MapPin, href: '/marketing/apple-tv/locations' },
          { label: 'Screens', icon: Tv, href: '/marketing/apple-tv/screens' },
          { label: 'Playlists', icon: ListVideo, href: '/marketing/apple-tv/playlists' },
          { label: 'Media', icon: Clapperboard, href: '/marketing/apple-tv/media' },
        ],
      },
      {
        // Beside the other outward-facing channels, and on the same module gate
        // as Social Media: Google Business IS a Zernio connection.
        //
        // A GROUP rather than a single row, so it reads like its siblings: the
        // sidebar renders an icon for a group and only a label for a leaf, which
        // left this one bare beside Ads Center, Campaigns, Apple TV and Social
        // Media. The three children are the three views of the section.
        label: 'Google Business',
        icon: Building2,
        href: '/marketing/google-business',
        // Its own module, not `zernio`: the data still travels over the
        // account's Zernio key, but review management is sold and enabled
        // separately from social publishing.
        requiresModuleActive: 'google business',
        requiredPermission: PERMISSION.MARKETING_CAMPAIGNS,
        children: [
          { label: 'Locations', icon: MapPin, href: '/marketing/google-business/locations' },
          { label: 'Overview', icon: BarChart2, href: '/marketing/google-business/overview' },
          { label: 'Reviews', icon: Star, href: '/marketing/google-business/reviews' },
        ],
      },
      {
        label: 'Social Media',
        icon: Share2,
        href: '/marketing/social',
        requiresModuleActive: 'zernio',
        requiredPermission: PERMISSION.MARKETING_CAMPAIGNS,
      },
      {
        // Corporate-provided PDF guides. Fail-CLOSED on an explicitly active
        // module row (opt-in per location, like Social Media), and needs only
        // the Marketing Access the parent already requires.
        label: 'Marketing Guides',
        icon: BookOpen,
        href: '/marketing/guides',
        requiresModuleActive: 'marketing guides',
      },
      {
        // Always last in Marketing, whichever module-gated entries are on: it has
        // no module gate of its own, so it is the one fixed point at the end.
        label: 'Content Bank',
        icon: Images,
        children: [
          { label: 'Photo Library', icon: FolderArchive, href: '/marketing/photos' },
          { label: 'Video Library', icon: Clapperboard, href: '/marketing/videos' },
        ],
      },
    ],
  },
  {
    label: 'Communications',
    icon: Mail,
    href: COMM_CENTER_NAV_HREF,
    requiredModuleKey: 'messaging',
    children: [
      { label: 'Unread', icon: Inbox, href: '/messages' },
      { label: 'Mentions', icon: AtSign, href: MENTIONS_NAV_HREF },
    ],
  },
  // Open to all staff. Visibility of the calls themselves is a per-user rule the
  // API owns (ScopesVideoCallVisibility): userLevel >= 3 sees the whole account,
  // everyone below sees only the calls they hosted. A `minUserLevel` here would
  // hide the section from the very people it is scoped for.
  {
    label: 'Video Calls',
    icon: Video,
    href: '/video-calls',
    requiredModuleKey: 'video',
    children: [
      { label: 'Analytics', icon: BarChart2, href: '/video-calls/analytics' },
    ],
  },
  {
    label: 'Tasks',
    icon: CheckSquare,
    href: '/tasks',
    requiredModuleKey: 'tasks',
    children: [
      { label: 'All Tasks', icon: CheckSquare, href: '/tasks/all' },
    ],
  },
  {
    label: 'Calendar',
    icon: Calendar,
    href: '/schedule',
    requiredModuleKey: 'scheduling',
    children: [
      { label: 'Day View', icon: CalendarDays, href: '/schedule?view=day' },
      { label: 'Week View', icon: CalendarDays, href: '/schedule?view=week' },
      { label: 'Appointments', icon: CalendarDays, href: '/appointments' },
    ],
  },
  {
    label: 'Forms',
    icon: ClipboardList,
    href: '/surveys/templates',
    requiredModuleKey: 'forms',
    children: [
      { label: 'All Forms', icon: ClipboardList, href: '/surveys/all' },
    ],
  },
  {
    label: 'Payments',
    icon: CreditCard,
    href: '/payments',
    requiredModuleKey: 'payments',
    children: [
      { label: 'All Payments', icon: CreditCard, href: '/payments/all' },
      { label: 'Credits', icon: Receipt, href: '/payments/credits' },
      { label: 'Gift Cards', icon: Gift, href: '/gift-cards' },
      // Orders placed through the client apps, tagged by which one. No
      // requiredModuleKey of its own: it inherits the parent's `payments` gate,
      // like All Payments and Credits above (unlike Allocations, which has a
      // second module of its own).
      { label: 'App Orders', icon: Smartphone, href: '/payments/app-orders' },
      // The deposits themselves, and what went into each one. Gated on its own
      // permission rather than the parent `payments` module: seeing what the
      // practice was paid is a narrower trust than taking a payment, and it is
      // granted per user (Admin > Users > Permissions).
      { label: 'Payouts', icon: Banknote, href: '/payments/payouts', requiredPermission: PERMISSION.PAYOUTS },
      { label: 'Allocations', icon: Layers, href: '/payments/allocations', requiredModuleKey: 'allocations' },
      // Forward chargebacks. Its own permission, separate from Payouts above:
      // answering a chargeback and reconciling a deposit are different jobs.
      { label: 'Disputes', icon: ShieldAlert, href: '/payments/disputes', requiredPermission: PERMISSION.DISPUTES },
    ],
  },
  {
    label: 'Memberships',
    icon: Crown,
    href: '/memberships',
    requiredModuleKey: 'memberships',
    children: [
      { label: 'All Memberships', icon: Crown, href: '/memberships/all' },
    ],
  },
  {
    label: 'Packages',
    icon: Package,
    href: '/packages',
    requiredModuleKey: 'packages',
    children: [
      { label: 'All Packages', icon: Package, href: '/packages/all' },
    ],
  },
  {
    label: 'Inventory',
    icon: Warehouse,
    href: '/inventory',
    requiredModuleKey: 'inventory',
    children: [
      { label: 'Stock', icon: Boxes, href: '/inventory/stock' },
      { label: 'Vendors', icon: UsersRound, href: '/inventory/vendors' },
      { label: 'Purchase Orders', icon: Receipt, href: '/inventory/purchase-orders' },
      { label: 'Reports', icon: BarChart2, href: '/inventory/reports' },
    ],
  },
  { label: 'Labs', icon: FlaskConical, href: '/lab-orders', minUserLevel: 2, requiredModuleKey: 'labs' },
  // ScriptSure's practice-level screens (Messages, Reports, the Audit Log, the
  // full application). Fail-CLOSED like its route in App.tsx: only an account
  // whose `daw` module row is explicitly ACTIVE sees it. No user-level gate
  // yet — gating it per user waits on the ScriptSure sign-in identity work.
  { label: 'Prescribing', icon: Pill, href: '/prescribing', requiresModuleActive: 'daw' },
  {
    label: 'Marketplace',
    icon: Store,
    // The heading opens the Marketplace dashboard (every app switched on for
    // the account); the children stay as direct links to what staff use.
    href: '/marketplace',
    // Hidden unless at least one Marketplace app is switched on.
    requiresAnyModuleActive: MARKETPLACE_MODULE_KEYS,
    children: [
      { label: 'Peptides', icon: Syringe, href: '/peptides', minUserLevel: 2, requiredModuleKey: 'axiom' },
      // Every Partell prescription across patients, like Peptides for Axiom.
      // Fail-CLOSED: Partell is opt-in, so only an account whose `partell`
      // module row is explicitly active sees it (same gate as its admin page).
      { label: 'Partell', icon: Pill, href: '/partell', minUserLevel: 2, requiresModuleActive: 'partell' },
      { label: 'Vitaboom', icon: Pill, href: '/vitaboom', minUserLevel: 2, requiresModuleActive: 'vitaboom' },
    ],
  },
  { label: 'Quality Assurance', icon: MessageSquare, href: '/qa', requiredModuleKey: 'qa' },
  {
    // The whole /reports tree used to be unreachable: this entry pointed only at
    // the admin page, and minUserLevel 3 hid it from everyone else — so a normal
    // user had no Reports item at all and could not find reports shared with them
    // or manage a schedule. The pages themselves are only module-gated
    // (App.tsx RequireModule), never admin-gated, so they work for any user.
    label: 'Reports',
    icon: BarChart2,
    href: '/reports/my-reports',
    requiredModuleKey: 'reports',
    // The Reports permission (15) gates this section PARTIALLY, on purpose. It is
    // not on the group: a user without it keeps My Reports, so they can still
    // open the reports they own or that were shared with them, and every run
    // underneath them. Everything that BUILDS or SCHEDULES a report is behind
    // the permission. The group's own href keeps it visible either way — see
    // filterNavItemByUserLevel, which drops a parent only when it has no href.
    //
    // THREE ITEMS, DELIBERATELY. This section carried six, and three of them
    // could not be told apart by the people using it: "Scheduled Reports",
    // "History" and "Manage" each listed a different slice of the same reports
    // with a different subset of the same actions, and nothing said which slice
    // you were looking at. They are gone. My Reports is now the single list of
    // everything — standard, custom, AI, one-off and recurring — with each
    // report's runs underneath it, which is what History was for.
    children: [
      // The single place a report is ordered, pre-built or custom. This page
      // existed before but no nav entry pointed at it, so the only way to reach
      // it was to cancel out of the report builder.
      { label: 'New Report', icon: FileSpreadsheet, href: '/reports', requiredPermission: PERMISSION.REPORTS },
      { label: 'My Reports', icon: FileText, href: '/reports/my-reports' },
      { label: 'Presets', icon: BookMarked, href: '/reports/presets', requiredPermission: PERMISSION.REPORTS },
    ],
  },
  {
    label: 'Help',
    icon: HelpCircle,
    href: '/help',
    children: [
      { label: 'My Training', icon: ClipboardList, href: '/knowledge' },
      { label: 'Library', icon: BookMarked, href: '/knowledge/library' },
      // User-facing archive of Prospera announcements (active + past/closed),
      // backed by account_alerts. Administration lives under Support.
      { label: 'Announcements', icon: Megaphone, href: '/announcements' },
    ],
  },
  {
    // Support is corporate-internal. Hidden from every customer admin —
    // gated by the strict `user.isSuperAdmin` flag, not userLevel.
    label: 'Support',
    icon: Ticket,
    requiresSuperAdmin: true,
    children: [
      { label: 'Queue', icon: HelpCircle, href: '/admin/support-tickets' },
      // Authoring for the Prospera-to-practices announcements (account_alerts)
      // that surface on the dashboard banner + Help > Announcements archive.
      { label: 'Announcements', icon: Megaphone, href: '/admin/account-alerts' },
    ],
  },
  {
    // Corp-curated training content. Gated on master_users.is_admin (a
    // weaker corp flag than Support's is_super_admin) rather than userLevel
    // — customer admins never manage this regardless of their level.
    label: 'Knowledge Base',
    icon: BookMarked,
    href: '/admin/knowledge-base',
    requiresMasterAdmin: true,
  },
  {
    label: 'Admin',
    icon: ShieldCheck,
    // Admin Access (permission 36) or level 5 — not a user level. Every group
    // below inherits this one gate; corp and module gates still apply per entry.
    // On top of those, every keyed entry (`adminPage`) needs the account's level
    // for it to be Editable or Locked (filterNavItemsByAdminPageLevels). A group
    // left empty disappears, and Admin with it.
    requiresAdminAccess: true,
    children: [
      {
        label: 'Integrations',
        icon: Plug,
        children: [
          { label: 'Marketing', icon: Megaphone, href: '/admin/marketing-integrations', adminPage: 'marketing-integrations', requiredModuleKey: 'marketing' },
          // Same module as the assistant itself: this page only configures the
          // AWS Bedrock account the assistant runs on, so it has no meaning
          // when the assistant is switched off.
          { label: 'AI', icon: Sparkles, href: '/admin/integrations/ai', adminPage: 'integrations/ai', requiredModuleKey: 'ai assistant' },
          { label: 'GetLabs', icon: FlaskConical, href: '/admin/integrations/getlabs', adminPage: 'integrations/getlabs', requiredModuleKey: 'getlabs' },
          { label: 'Google Calendar', icon: CalendarDays, href: '/admin/integrations/google-calendar', adminPage: 'integrations/google-calendar',requiredModuleKey:'scheduling' },
          // Corp super admins only, on the child: an integration lets an outside
          // system file labs on patient charts, and a practice can grant its own
          // users level 5. Matches RequireSuperAdmin on the route and
          // EnsureSuperAdmin on /v5/lab-upload-integrations.
          { label: 'Lab Uploads', icon: FlaskConical, href: '/admin/integrations/lab-uploads', requiresSuperAdmin: true },
          { label: 'Quest', icon: FlaskConical, href: '/admin/integrations/quest', adminPage: 'integrations/quest', requiredModuleKey: 'labs' },
          // Fail-closed: Vibrant ORDERING is opt-in. Results display stays on
          // the fail-open 'vibrant' module; the admin/order surfaces appear
          // only when 'vibrant ordering' is explicitly active. The href keeps
          // its /admin/marketplace/ path so existing links stay valid.
          { label: 'Vibrant', icon: FlaskConical, href: '/admin/marketplace/vibrant', adminPage: 'marketplace/vibrant', requiresModuleActive: 'vibrant ordering' },
          // Fail-closed like Vibrant ordering: shown only when the Access Labs
          // module is explicitly active. Results stay visible either way (R2).
          { label: 'Access Labs', icon: FlaskConical, href: '/admin/integrations/access', adminPage: 'integrations/access', requiresModuleActive: ACCESS_LABS_MODULE_KEY },
          // Lab Decks (the decks the Quest results viewer shows) is hidden for now
          // (2026-10-16): out of this menu and the Admin page catalog, so no
          // account has a level for it and its route redirects. The page, route,
          // API and decks all stay, and the viewer still shows the decks. To show
          // it again, put back this leaf and its catalog entry (both copies):
          //   { label: 'Lab Decks', icon: TestTubes, href: '/admin/lab-decks', adminPage: 'lab-decks', requiredModuleKey: 'labs' },
          // Fail-open like its siblings: with no `modules` row for TreatRx at
          // all, the key never enters the access map and the entry stays visible.
          { label: 'TreatRx', icon: Pill, href: '/admin/integrations/treatrx', adminPage: 'integrations/treatrx', requiredModuleKey: 'treatrx' },
          // Fail-OPEN like its siblings, and deliberately so: this is where a
          // practice ENTERS its WhatsApp number. Gating the setup page on the
          // module being active made setup unreachable until someone had
          // already switched WhatsApp on. The gate belongs on SENDING — see
          // CommunicationsSendController::sendSms and the comm-log affordances,
          // which stay fail-closed on `explicit`.
          // Legacy's Twilio Settings: the practice's own Twilio account (SMS,
          // calls, WhatsApp). No module: every practice sends through Twilio.
          { label: 'Twilio', icon: Phone, href: '/admin/integrations/twilio', adminPage: 'integrations/twilio' },
          { label: 'WhatsApp', icon: MessageCircle, href: '/admin/integrations/whatsapp', adminPage: 'integrations/whatsapp', requiredModuleKey: 'whatsapp' },
        ],
      },
      {
        label: 'Marketplace',
        icon: Store,
        children: [
          { label: 'Axiom', icon: FlaskConical, href: '/admin/marketplace/axiom', adminPage: 'marketplace/axiom', requiresModuleActive: 'axiom' },
          { label: 'SiPhox Health', icon: FlaskConical, href: '/admin/marketplace/siphox', adminPage: 'marketplace/siphox', requiresModuleActive: 'siphox' },
          { label: 'BodySpec', icon: FlaskConical, href: '/admin/marketplace/bodyspec', adminPage: 'marketplace/bodyspec', requiresModuleActive: 'bodyspec' },
          { label: 'TruDiagnostic', icon: FlaskConical, href: '/admin/marketplace/trudiagnostic', adminPage: 'marketplace/trudiagnostic', requiresModuleActive: 'trudiagnostics' },
          { label: 'Genova', icon: FlaskConical, href: '/admin/marketplace/genova', adminPage: 'marketplace/genova', requiresModuleActive: 'genova diagnostics' },
          { label: 'mescreen', icon: FlaskConical, href: '/admin/marketplace/mescreen', adminPage: 'marketplace/mescreen', requiresModuleActive: 'mescreen' },
          { label: 'Partell Pharmacy', icon: FlaskConical, href: '/admin/marketplace/rmm', adminPage: 'marketplace/rmm', requiresModuleActive: 'partell' },
          // Fail-closed like Facilities/Vibrant: the Vitaboom marketplace is
          // opt-in, so the entry appears only when the 'vitaboom' module row
          // is explicitly ACTIVE.
          { label: 'Vitaboom', icon: Pill, href: '/admin/marketplace/vitaboom', adminPage: 'marketplace/vitaboom', requiresModuleActive: 'vitaboom' },
        ],
      },
      // The groups below follow the corporate dashboard's sections (Account,
      // Patients, Products, Memberships, Plans, Appointments, Tasks, Forms &
      // Templates, Payments, Users), plus Prospera's own Security and Quotes.
      {
        label: 'Account',
        icon: Building2,
        children: [
          // The account's own settings (corp's Edit Account fields) and its
          // read-only summary — legacy admin_account_details.php.
          { label: 'Details', icon: ClipboardList, href: '/admin/account-summary', adminPage: 'account-summary' },
          { label: 'Scorecards', icon: ClipboardCheck, href: '/admin/scorecards', adminPage: 'scorecards', requiredModuleKey: 'crm' },
          // Subscribe billing methods (parity with legacy admin_billing_list.php).
          // Distinct from the simple `/admin/billing` settings page — this one
          // manages the cards Prospera bills the practice with each month.
          { label: 'Billing', icon: Receipt, href: '/admin/billing-methods', adminPage: 'billing-methods' },
          { label: 'Branding', icon: Palette, href: '/admin/branding', adminPage: 'branding' },
          // The practice's own dashboard alerts (corp's Account Alerts). Prospera's
          // group-wide announcements are Support > Announcements.
          { label: 'Account Alerts', icon: BellRing, href: '/admin/alerts', adminPage: 'alerts' },
          { label: 'Patient Portal', icon: MonitorSmartphone, href: '/admin/patient-portal', adminPage: 'patient-portal' },
          // Fail-closed: appears only for accounts whose 'facilities' module
          // row is explicitly ACTIVE (restricted/peptides-only accounts).
          { label: 'Facilities', icon: Building2, href: '/admin/facilities', adminPage: 'facilities', requiresModuleActive: 'facilities' },
          { label: 'Common Links', icon: Link2, href: '/admin/common-links', adminPage: 'common-links' },
          { label: 'Languages', icon: Languages, href: '/admin/languages', adminPage: 'languages' },
        ],
      },
      {
        label: 'Security',
        icon: ShieldCheck,
        children: [
          // Parity with legacy admin_ip_addresses.php. Gated by Admin Access
          // like the rest of Admin, no longer by a user level.
          {
            label: 'Access Restrictions',
            icon: ShieldCheck,
            href: '/admin/access-restrictions', adminPage: 'access-restrictions',
          },
        ],
      },
      {
        label: 'Patients/Leads',
        icon: Contact,
        children: [
          { label: 'Patient Notes', icon: FileText, href: '/admin/note-templates', adminPage: 'note-templates', requiredModuleKey: 'messaging' },
          { label: 'Patient Tags', icon: Tags, href: '/admin/patient-tags', adminPage: 'patient-tags' },
          { label: 'Lead Sources', icon: Search, href: '/admin/referrers', adminPage: 'lead-sources', requiredModuleKey: 'crm' },
          { label: 'Lead Fields', icon: FormInput, href: '/admin/lead-fields', adminPage: 'lead-fields', requiredModuleKey: 'crm' },
          { label: 'New Patient Form', icon: FormInput, href: '/admin/lead-form', adminPage: 'lead-form', requiredModuleKey: 'crm' },
          { label: 'Q/A Reasons', icon: HelpCircle, href: '/admin/qa-reasons', adminPage: 'qa-reasons', requiredModuleKey: 'qa' },
          { label: 'V.I.P. Program', icon: Star, href: '/admin/vip-program', adminPage: 'vip-program' },
        ],
      },
      {
        label: 'Products',
        icon: Package,
        children: [
          { label: 'Products', icon: ShoppingBag, href: '/admin/products', adminPage: 'products', requiredModuleKey: 'inventory' },
          { label: 'Product Types', icon: Boxes, href: '/admin/product-types', adminPage: 'product-types', requiredModuleKey: 'inventory' },
          { label: 'Packages', icon: Package, href: '/admin/packages', adminPage: 'packages', requiredModuleKey: 'packages' },
          { label: 'Shipping Fees', icon: Truck, href: '/admin/shipping-fees', adminPage: 'shipping-fees', requiredModuleKey: 'inventory' },
          // Legacy listed Procedures only with the appointments add-on (scheduling).
          { label: 'Procedures', icon: Stethoscope, href: '/admin/procedures', adminPage: 'procedures', requiredModuleKey: 'scheduling' },
        ],
      },
      {
        label: 'Memberships',
        icon: Crown,
        children: [
          { label: 'Memberships', icon: Crown, href: '/admin/memberships', adminPage: 'memberships', requiredModuleKey: 'memberships' },
          { label: 'Agreements', icon: FileText, href: '/admin/membership-agreements', adminPage: 'membership-agreements', requiredModuleKey: 'memberships' },
          // Membership cancellation reasons (the corp page).
          // Short, as Agreements is, so the label fits on one line in the sidebar.
          { label: 'Cancel Reasons', icon: XCircle, href: '/admin/membership-cancel-reasons', adminPage: 'membership-cancel-reasons', requiredModuleKey: 'memberships' },
        ],
      },
      {
        label: 'Plans',
        icon: ScrollText,
        children: [
          { label: 'Plan Types', icon: Clipboard, href: '/admin/plan-types', adminPage: 'plan-types', requiredModuleKey: 'crm' },
          { label: 'Plan Roles', icon: UserCheck, href: '/admin/plan-roles', adminPage: 'plan-roles' },
          { label: 'Plan Statuses', icon: ListChecks, href: '/admin/plan-statuses', adminPage: 'plan-statuses', requiredModuleKey: 'crm' },
          { label: 'Stage Pipeline', icon: Layers, href: '/admin/stages', adminPage: 'stages', requiredModuleKey: 'crm' },
        ],
      },
      {
        label: 'Quotes',
        icon: FileText,
        children: [
          // Both were commented out while Quotes had no owning module and the
          // Discounts screen called an endpoint that did not exist. Quotes is
          // opt-in, so they use requiresModuleActive rather than the fail-open
          // requiredModuleKey — a practice without the module sees neither.
          { label: 'Quote Presets', icon: FileText, href: '/admin/quote-presets', adminPage: 'quote-presets', requiresModuleActive: 'quotes' },
          { label: 'Quote Discounts', icon: Percent, href: '/admin/quote-discounts', adminPage: 'quote-discounts', requiresModuleActive: 'quotes' },
        ],
      },
      {
        label: 'Appointments',
        icon: CalendarDays,
        children: [
          { label: 'Appointment Types', icon: CalendarDays, href: '/admin/appointments', adminPage: 'appointments', requiredModuleKey: 'scheduling' },
          { label: 'Confirmations', icon: CheckSquare, href: '/admin/appointment-confirmations', adminPage: 'appointment-confirmations', requiredModuleKey: 'scheduling' },
          { label: 'Resources', icon: Warehouse, href: '/admin/appointment-resources', adminPage: 'appointment-resources', requiredModuleKey: 'scheduling' },
          { label: 'Time Blocks', icon: Timer, href: '/admin/time-blocks', adminPage: 'time-blocks', requiredModuleKey: 'scheduling' },
          { label: 'Calendar Settings', icon: Calendar, href: '/admin/calendar-settings', adminPage: 'calendar-settings', requiredModuleKey: 'scheduling' },
        ],
      },
      {
        label: 'Tasks',
        icon: ListChecks,
        children: [
          { label: 'Tasks', icon: ListChecks, href: '/admin/tasks', adminPage: 'tasks', requiredModuleKey: 'tasks' },
          { label: 'Task Groups', icon: Layers, href: '/admin/task-groups', adminPage: 'task-groups', requiredModuleKey: 'tasks' },
        ],
      },
      {
        label: 'Forms/Templates',
        icon: ClipboardList,
        children: [
          // Corp's order. The API gates /surveys on the forms module; the entry
          // follows. The SMS / email templates are the account's own (comm log,
          // e-forms, Tasks), not Marketing's, so no module (SCRUM-2434).
          // Documents and the packets built from them, one section as in legacy.
          { label: 'Documents', icon: FolderArchive, href: '/admin/documents', adminPage: 'documents' },
          { label: 'E-Forms', icon: FileSpreadsheet, href: '/admin/eforms', adminPage: 'eforms', requiredModuleKey: 'forms' },
          { label: 'SMS Templates', icon: MessageCircle, href: '/admin/sms-templates', adminPage: 'sms-templates' },
          { label: 'Email Templates', icon: Mail, href: '/admin/email-templates', adminPage: 'email-templates' },
          // Legacy's E-mail Header & Footer: tasks and auto responders use them too, so no module.
          { label: 'Header & Footer', icon: MailOpen, href: '/admin/email-header-footer', adminPage: 'email-header-footer' },
          // Level 3 comes from the Admin group, the same line the page's API draws.
          { label: 'Auto Responders', icon: Zap, href: '/admin/auto-responders', adminPage: 'auto-responders' },
          { label: 'Print Templates', icon: Printer, href: '/admin/print-templates', adminPage: 'print-templates' },
          { label: 'Disclosures', icon: FileWarning, href: '/admin/disclosures', adminPage: 'disclosures' },
        ],
      },
      {
        label: 'Payments',
        icon: CreditCard,
        children: [
          // The payment types the practice accepts (cash, check, financing…).
          { label: 'Payment Methods', icon: CreditCard, href: '/admin/payment-methods', adminPage: 'payment-methods', requiredModuleKey: 'payments' },
          // Legacy's Payment Credit Types and Loan Types.
          { label: 'Credit Types', icon: Wallet, href: '/admin/credit-types', adminPage: 'credit-types', requiredModuleKey: 'payments' },
          { label: 'Loan Types', icon: FileSignature, href: '/admin/loan-types', adminPage: 'loan-types', requiredModuleKey: 'payments' },
          // Their own modules, not `payments`: a practice can run payments
          // while withholding discounting and promo codes from staff.
          { label: 'Discounts', icon: Percent, href: '/admin/payment-discounts', adminPage: 'payment-discounts', requiredModuleKey: 'discounts' },
          { label: 'Promo Codes', icon: Ticket, href: '/admin/promo-codes', adminPage: 'promo-codes', requiredModuleKey: 'promo codes' },
          // Presets, limits, terms and the online switches for gift cards;
          // gift cards sit under Payments, so this shares its module.
          { label: 'Gift Card Settings', icon: Gift, href: '/admin/gift-card-settings', adminPage: 'gift-card-settings', requiredModuleKey: 'payments' },
          // The Forward merchant accounts — the gateway credentials themselves.
          // Admin-only on purpose: level 3 comes from the Admin group above,
          // matching the legacy merchant page, which requires level 3 to save.
          //
          // Payouts and Disputes deliberately do NOT live here. They briefly did,
          // and that gated them on the admin level; they are back under Payments
          // with a permission each, which is the access model they needed.
          {
            label: 'Forward',
            icon: Landmark,
            requiredModuleKey: 'payments',
            children: [
              { label: 'Merchants', icon: Landmark, href: '/admin/payments/forward', adminPage: 'payments/forward' },
              { label: 'POS Terminals', icon: TerminalSquare, href: '/admin/pos-terminals', adminPage: 'pos-terminals' },
            ],
          },
        ],
      },
      {
        label: 'Users',
        icon: Users,
        children: [
          { label: 'User Details', icon: UserCog, href: '/admin/users', adminPage: 'users' },
          { label: 'User Groups', icon: UsersRound, href: '/admin/user-groups', adminPage: 'user-groups', requiredModuleKey: 'user groups' },
          // Admin Access comes from the Admin group. On top of it, the page's
          // level, level 3 and the permission (R18); the server checks level 3
          // and the grant again, live, on every list and download.
          { label: 'User Audit', icon: ScrollText, href: '/admin/user-audit', adminPage: 'user-audit', minUserLevel: 3, requiredPermission: PERMISSION.USER_AUDIT },
        ],
      },
      {
        // Import tools. The GROUP inherits Admin's level 3, because the Leads
        // import writes only into the caller's own account. Each child carries
        // its own gate: Leads needs CRM plus the opt-in Lead Import module, and
        // the strict corp gate sits on the Zenoti child, which migrates data
        // into accounts the operator is not a member of.
        //
        // The group deliberately has NO href: /admin/import is the Zenoti
        // landing page and stays super-admin-only, so a practice admin clicking
        // the group would be bounced to /help. Href-less matches its siblings
        // (Users, Templates, Appointments) and hands the collapse rule below
        // (`children.length === 0 && !item.href`) responsibility for hiding the
        // group when nothing inside it survives.
        label: 'Import',
        icon: DatabaseZap,
        children: [
          {
            label: 'Leads',
            icon: UserPlus,
            href: '/admin/import/leads', adminPage: 'import/leads',
            requiredModuleKey: 'crm',
            // Opt-in, like Zenoti below: an account without an ACTIVE Lead
            // Import row does not see the item. Keep in step with
            // LEAD_IMPORT_MODULE_KEY in lib/leadImport.ts.
            requiresModuleActive: 'lead import',
          },
          {
            label: 'Zenoti',
            icon: DatabaseZap,
            href: '/admin/import/zenoti',
            requiresSuperAdmin: true,
            // Fail-CLOSED. `requiredModuleKey` reads a missing pivot row as
            // ENABLED, which is right for platform areas every practice keeps
            // and wrong for corp tooling nobody is entitled to by default: a
            // never-toggled account gets the migration tool for free. It also
            // serves `enabled` straight from the persisted authStore seed with
            // no freshness check (only `explicit` is gated — see
            // useAccountModules.ts), so a module switched off in Corp-App can
            // stay visible for the rest of a session.
            requiresModuleActive: 'zenoti import',
          },
        ],
      },
    ],
  },
];

function filterNavItemByUserLevel(
  item: NavItemDef,
  userLevel: number,
  isSuperAdmin: boolean,
  isMasterAdmin: boolean,
  permissions: number[] = [],
): NavItemDef | null {
  if (item.requiresSuperAdmin && !isSuperAdmin) {
    return null;
  }
  if (item.requiresMasterAdmin && !isMasterAdmin) {
    return null;
  }
  if (item.requiresAdminAccess && !hasAdminAccess(userLevel, permissions)) {
    return null;
  }
  if (item.minUserLevel && userLevel < item.minUserLevel) {
    return null;
  }
  if (item.requiredPermission && !permissions.includes(item.requiredPermission)) {
    return null;
  }

  if (!item.children) {
    return item;
  }

  const children = item.children
    .map((child) => filterNavItemByUserLevel(child, userLevel, isSuperAdmin, isMasterAdmin, permissions))
    .filter((child): child is NavItemDef => child !== null);

  if (children.length === 0 && !item.href) {
    return null;
  }

  return {
    ...item,
    children,
  };
}

export function filterNavItemsByUserLevel(
  items: NavItemDef[],
  userLevel: number,
  isSuperAdmin = false,
  isMasterAdmin = false,
  permissions: number[] = [],
): NavItemDef[] {
  return items
    .map((item) => filterNavItemByUserLevel(item, userLevel, isSuperAdmin, isMasterAdmin, permissions))
    .filter((item): item is NavItemDef => item !== null);
}

/**
 * Per-account module access from Prospera-Corp-App's Module Settings,
 * keyed by the module's base path (module key "marketing" → "/marketing").
 * `disabledSections` holds route-segment slugs under that base
 * (e.g. "push-campaigns" hides /marketing/push-campaigns).
 */
export interface ModuleAccessMap {
  [basePath: string]: {
    enabled: boolean;
    /** An ACTIVE aa_accounts_modules row exists — drives `requiresModuleActive`. */
    explicit?: boolean;
    disabledSections: string[];
  };
}

function sectionSlug(href: string, basePath: string): string | null {
  if (!href.startsWith(`${basePath}/`)) return null;
  return href.slice(basePath.length + 1).split(/[/?#]/)[0] || null;
}

function filterNavItemByModuleAccess(item: NavItemDef, access: ModuleAccessMap): NavItemDef | null {
  // Fail-closed opt-in gate: the entry needs an explicitly ACTIVE module row.
  // Absent from the map (or not explicit) = hidden — the inverse default of
  // requiredModuleKey below, for surfaces like Facilities that only restricted
  // accounts should ever see.
  if (item.requiresModuleActive && !access[`/${item.requiresModuleActive}`]?.explicit) {
    return null;
  }
  // "Any of these" gate on a group's OWN page: with none active, the group
  // keeps working exactly as it did without the page — a plain expander over
  // whatever children survive (Peptides is fail-open, so it often does), and
  // it disappears only when nothing is left.
  if (item.requiresAnyModuleActive && !item.requiresAnyModuleActive.some((k) => access[`/${k}`]?.explicit)) {
    const { href: _ownPage, requiresAnyModuleActive: _gate, ...rest } = item;
    void _ownPage; void _gate;
    return filterNavItemByModuleAccess(rest, access);
  }

  // Explicit module tag (for entries whose href doesn't share the module base
  // path, e.g. /admin/marketplace/vibrant). Absent from the map = enabled.
  if (item.requiredModuleKey) {
    const tagged = access[`/${item.requiredModuleKey}`];
    if (tagged && !tagged.enabled) return null;
  }

  for (const [basePath, mod] of Object.entries(access)) {
    if (!item.href) continue;
    if (item.href !== basePath && !item.href.startsWith(`${basePath}/`)) continue;
    if (!mod.enabled) return null;
    // The `admin` module's section switches are retired: Admin pages follow
    // their per-page level instead (filterNavItemsByAdminPageLevels). Values
    // stored before that stay in the database unread — matching them here would
    // hide every /admin/integrations/* page for a stored `integrations` switch,
    // even at Editable.
    if (basePath === '/admin') continue;
    const slug = sectionSlug(item.href, basePath);
    if (slug && mod.disabledSections.includes(slug)) {
      // A group whose own landing page is hidden can survive through its
      // visible children (handled below); a leaf is simply dropped.
      if (!item.children?.length) return null;
      const children = item.children
        .map((c) => filterNavItemByModuleAccess(c, access))
        .filter((c): c is NavItemDef => c !== null);
      if (children.length === 0) return null;
      return { ...item, href: children[0].href, children };
    }
  }

  if (!item.children) return item;
  const children = item.children
    .map((c) => filterNavItemByModuleAccess(c, access))
    .filter((c): c is NavItemDef => c !== null);
  if (children.length === 0 && !item.href) return null;
  return { ...item, children };
}

export function filterNavItemsByModuleAccess(items: NavItemDef[], access: ModuleAccessMap): NavItemDef[] {
  // Always traverse — even with an empty map — so `requiresModuleActive`
  // (fail-closed) items are dropped when nothing is explicitly active.
  // Untagged items are untouched by an empty map, same as the old early return.
  return items
    .map((item) => filterNavItemByModuleAccess(item, access))
    .filter((item): item is NavItemDef => item !== null);
}

function filterNavItemByAdminPageLevels(item: NavItemDef, levels: AdminPageLevelMap | null): NavItemDef | null {
  if (item.adminPage && (!levels || adminPageLevelOf(levels, item.adminPage) === 'hidden')) {
    return null;
  }
  if (!item.children) return item;
  const children = item.children
    .map((c) => filterNavItemByAdminPageLevels(c, levels))
    .filter((c): c is NavItemDef => c !== null);
  if (children.length === 0 && !item.href) return null;
  return { ...item, children };
}

/**
 * Admin page levels (per account, set by super admins): a keyed entry
 * (`adminPage`) stays only when its level is Editable or Locked. `levels` is
 * the FRESH map from useAdminPageLevels, or null while it is not fresh
 * (loading, the persisted seed alone, a failed fetch) — then every keyed entry
 * drops. A missing key or any other value is Hidden. Unkeyed entries are
 * untouched; groups left with nothing (and no href of their own) collapse, so
 * Admin itself disappears when no page is left.
 */
export function filterNavItemsByAdminPageLevels(
  items: NavItemDef[],
  levels: AdminPageLevelMap | null,
): NavItemDef[] {
  return items
    .map((item) => filterNavItemByAdminPageLevels(item, levels))
    .filter((item): item is NavItemDef => item !== null);
}

/** First navigable href in a filtered tree (depth-first). Used by the
 *  safe-landing resolver (useNavAccess) when a module gate blocks a route. */
export function firstNavigableHref(items: NavItemDef[]): string | null {
  for (const item of items) {
    if (item.href && !item.external) return item.href;
    if (item.children) {
      const child = firstNavigableHref(item.children);
      if (child) return child;
    }
  }
  return null;
}

export function getHelpNavItems(
  userLevel = 0,
  isSuperAdmin = false,
  isMasterAdmin = false,
  permissions: number[] = [],
): NavItemDef[] {
  return filterNavItemsByUserLevel(NAV_ITEMS, userLevel, isSuperAdmin, isMasterAdmin, permissions).filter(
    (item) => item.label !== 'Help',
  );
}
